/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: Object component for cm.GeneralAgency. Owns its own navigation state (the generic
 *         object page host never writes activeNavigationItems back) and renders the whole
 *         Generalagentur maintenance surface — agency header, level-1 partner numbers,
 *         level-2 assignments and the agency-wide agents — on a single page.
 */
import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {MvsObjectService} from '../../../../../core/shared/service/object/mvs-object.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from '../../../../../core/shared/logic/object-change-information';
import {GeneralAgencyDto} from '../../../model/dto/entity/general-agency.dto';

/**
 * ObjectBaseComponent for the Generalagentur config entity (cm.GeneralAgency). It is registered
 * as the objectComponent of cm.GeneralAgency in cm.entity-provider.ts, so a row click on the flat
 * Generalagentur list of the CM config page opens this component through the generic object page.
 *
 * Navigation state ownership: the actually used host (MvsGenericObjectPageComponent) never writes
 * activeNavigationItems back onto the component instance — only the sidebar host ObjectComponent
 * does. This component therefore seeds the default section itself and subscribes to the shell's
 * ?navItem query parameter, which is the only channel through which the shell switches sections.
 *
 * Sections:
 *  - "Partner & Berater" (default, action agencyPartners): the single-page maintenance surface.
 *    Agency header, level 1 = cm.GeneralAgencyNumber (partner definition, row selectable),
 *    level 2 = the assignments of the selected number (cm.GeneralAgencyNumberAgent,
 *    cm.GeneralAgencyNumberGroup, cm.GeneralAgencyNumberContractType) and, below both, the
 *    agency-wide cm.GeneralAgencyAgent list.
 *  - "Stammdaten" (action stammdaten): the full metadata-driven entity-data form of the agency.
 *
 * The nav action is deliberately named agencyPartners and NOT partner: the object page renders in
 * place of the config page and both share one single ?navItem parameter on the same route, where
 * cm-config.page.ts already owns the action partner.
 *
 * Widget lifecycle: the master widgets (agency header, partner numbers, agency-wide agents) are
 * built once per agency id and keep their instances afterwards, so sorting, filtering and the row
 * marker survive every DTO reload. Only the three level-2 detail widgets are rebuilt, and they are
 * rebuilt exclusively from a level-1 selection event — the data flow is strictly one-way.
 *
 * Event wiring: the component reacts to exactly two framework events and to no timer.
 *  - handleAgencyDataLoaded() on the header widget's (onLoadData) reloads the component DTO after
 *    an inline save, so the page title follows the saved name.
 *  - the objectChangeEvent subscription drops the level-2 selection when the selected partner
 *    number is deleted.
 * postObjectObservationRefresh() is deliberately NOT overridden: ObserverService is a mouse
 * activity poll (informActivity() on mouseenter/mousemove, a 1s interval that re-emits every ~4s
 * while the pointer rests on the component), not a change notification, so overriding it would
 * turn a hover into an endless GET loop on the agency.
 *
 * All lists use WidgetFactory table widgets, whose framework toolbar renders the generic
 * metadata-driven CRUD form and defaults the parent foreign key — no hand-written Angular forms,
 * and no enum-to-label mapping in TypeScript (labels come from the backend @UiEnumValueInfo
 * metadata through the framework).
 */
@Component({
    selector: 'mvs-cm-general-agency-object-component',
    templateUrl: './cm-general-agency-object.component.html',
    styleUrls: ['./cm-general-agency-object.component.scss'],
    standalone: false
})
export class CmGeneralAgencyObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    /** Nav action of the single-page maintenance section. */
    static readonly cActionAgencyPartners: string = 'agencyPartners';

    /** Nav action of the master data section. */
    static readonly cActionMasterData: string = 'stammdaten';

    /** Object type of the level-1 entity; the deletion subscription filters on it. */
    static readonly cObjectTypeNumber: string = 'cm.GeneralAgencyNumber';

    /**
     * Nav action of the Generalagentur section of the CM config page — see the navigationItems of
     * cm-config.page.ts, where this very string selects the flat cm.GeneralAgency list.
     *
     * It has to be known here because the object page renders IN PLACE of the config page on the
     * SAME route (/cm/config): both read one single shared ?navItem query parameter. While this
     * component is open the parameter carries one of ITS actions, which the config page cannot
     * resolve — PageComponent.subscribeToParams() sets no default when navItem is present but
     * unmatched, so its template, gated on @if (initialized && activeNavigationItem), would render
     * an empty page once the object is closed. Closing therefore restores this value.
     */
    private static readonly cConfigPageNavItem: string = 'cm.GeneralAgency';

    /**
     * Route the restore is bound to, i.e. the route the config page and this object page share
     * (cm.route.ts: {path: 'cm/config'}, mounted under the empty parent path of the lazy CRM
     * routing module, so the serialized URL is exactly this).
     */
    private static readonly cConfigPageRoute: string = '/cm/config';

    /** Name of the shared query parameter that carries the section of both pages. */
    private static readonly cNavItemQueryParam: string = 'navItem';

    /**
     * Query parameter that marks an open object page. MvsObjectNavigationService writes it as
     * "{objectType}:{objectId}[:{navigationType}]" when an object is opened and nulls it when the
     * object is closed, and Angular drops null valued parameters from the URL (Router
     * .removeEmptyProps). Its presence is therefore the reliable "an object page is open" flag.
     */
    private static readonly cObjectPageQueryParam: string = 'tid';

    /**
     * Template facing aliases of the two binding nav actions. The template decides what renders,
     * so it must compare against the constants — a raw literal would keep compiling after a rename
     * and silently produce a blank content area.
     */
    readonly cActionAgencyPartners: string = CmGeneralAgencyObjectComponent.cActionAgencyPartners;
    readonly cActionMasterData: string = CmGeneralAgencyObjectComponent.cActionMasterData;

    navigationItems: NavigationItem[] = [
        {
            label: 'Partner & Berater',
            action: CmGeneralAgencyObjectComponent.cActionAgencyPartners,
            tooltip: 'Partnernummern und Beraterzuweisung',
            icon: 'fa-regular fa-handshake',
            toggleable: false,
            default: true
        },
        {
            label: 'Stammdaten',
            action: CmGeneralAgencyObjectComponent.cActionMasterData,
            tooltip: 'Stammdaten der Generalagentur',
            icon: 'fa-regular fa-building-user',
            toggleable: false,
            default: false
        }
    ];

    // ---------------------------------------------------------------------------------------
    // Master widgets — built once per agency id, reloaded in place afterwards.
    // ---------------------------------------------------------------------------------------

    /** Entity data of the agency itself; serves as the header AND as the Stammdaten section. */
    generalAgencyWidget: WidgetData;

    /** Level 1: partner numbers (cm.GeneralAgencyNumber) of this agency, row selectable. */
    numbersWidget: WidgetData;

    /** Agency-wide, partner independent agent assignments (cm.GeneralAgencyAgent). */
    agentsWidget: WidgetData;

    // ---------------------------------------------------------------------------------------
    // Level 2 — rebuilt on every level-1 selection.
    // ---------------------------------------------------------------------------------------

    /** Id of the selected partner number; drives the whole level-2 area. */
    selectedNumberId: number;

    /** Read-only caption of the selected partner number, taken from the selection event. */
    selectedNumberLabel: string;

    /** Active level-2 tab; 0 = Berater (default), 1 = Sparten, 2 = Vertragstypen. */
    numberDetailTabIndex: number = 0;

    numberAgentsWidget: WidgetData;
    numberGroupsWidget: WidgetData;
    numberContractTypesWidget: WidgetData;

    /**
     * True when the agency DTO could not be loaded. ObjectBaseComponent.postRefreshObjectError()
     * clears dto and initialized, so without this flag the template could not tell a failed load
     * apart from a load that is still running and would show the loading state forever.
     */
    loadError: boolean = false;

    /**
     * Message of the error state. It lives on the component and not in the template because the
     * sentence is longer than the maximum line length allowed for a source line, and an HTML
     * attribute value cannot be split across lines without changing the rendered text.
     */
    readonly loadErrorText: string = 'Die Generalagentur konnte nicht geladen werden. '
        + 'Bitte laden Sie die Seite neu oder wenden Sie sich an den Support.';

    /** Agency id the master widgets were built for; guards the build-once rule. */
    private masterWidgetAgencyId: number;

    /**
     * False until the header widget reported its very first load. That first load carries exactly
     * the data the component DTO was just built from, so reacting to it would only issue a
     * duplicate GET on the agency.
     */
    private agencyWidgetLoaded: boolean = false;

    private queryParamSubscription: Subscription;

    private objectChangeSubscription: Subscription;

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        private route: ActivatedRoute,
        private router: Router,
        private objectService: MvsObjectService) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
        this.subscribeToNumberDeletion();
    }

    /**
     * Called when the DTO is loaded or refreshed. Seeds the default section exactly once, so a
     * later DTO reload never throws the user back out of the section they navigated to, and
     * builds the master widgets on the first load of a given agency.
     *
     * The nav items are published UNCONDITIONALLY. The host learns them only from this emission,
     * and it rebuilds this component from scratch on every objectIdentifier change. On such a
     * fresh instance the ?navItem subscription can have set activeNavigationItems already before
     * the DTO arrives — publishing inside the seed guard would then skip the emission and the
     * host would render no navigation at all.
     */
    override onObjectChanged(): void {
        this.loadError = false;

        const dto = this.dto as GeneralAgencyDto;
        this.setPageTitle(dto?.name ?? 'Generalagentur');

        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems.find(item => item.default);
        }

        this.onNavigationItems.emit(this.navigationItems);

        this.createMasterWidgets();
    }

    /**
     * Load failures end up here. The base implementation shows a toast and resets dto/initialized,
     * which alone is indistinguishable from a load still in flight — the flag lets the template
     * render a real error state with a way out instead of an endless loading state.
     */
    override postRefreshObjectError(error: any): void {
        super.postRefreshObjectError(error);
        this.loadError = true;
    }

    /**
     * Reloads the agency DTO whenever the header widget reloaded its own data. The header widget
     * refreshes itself after an inline save (it subscribes to objectChangeEvent and re-runs its
     * data request), but the component's own dto — and with it the page title — would otherwise
     * keep the values the component was opened with.
     *
     * This is the change driven counterpart of the observer poll that ObjectBaseComponent offers
     * through postObjectObservationRefresh(): that hook is fed by ObserverService, which is a mouse
     * activity timer and not a change notification, so it would reload the agency every few seconds
     * for as long as the pointer rests on the component. It is therefore left untouched.
     *
     * The very first load after the widget was built is skipped because it delivers exactly the
     * state the DTO was just built from; every later load reloads the DTO. Switching sections
     * remounts the widget and therefore costs one additional read — a bounded, user triggered cost,
     * unlike a timer.
     *
     * The reload itself is what the build-once guard in createMasterWidgets() protects the lists
     * against: the master widgets keep their instances, so sorting, filtering and the level-1 row
     * marker — and therefore the whole level-2 area — survive it, and no further load is triggered.
     */
    handleAgencyDataLoaded(): void {

        if (!this.agencyWidgetLoaded) {
            this.agencyWidgetLoaded = true;
            return;
        }

        this.refreshObject(false);
    }

    /**
     * Supports the shell's section switching, which happens exclusively through the ?navItem query
     * parameter. Foreign values (the config page keeps its own navItem on the very same route) are
     * ignored, which leaves the seeded default section active.
     *
     * Leaving the maintenance section destroys the level-1 table (the sections are @if gated), so
     * the level-2 selection is reset here — otherwise the detail area would keep showing a number
     * that the rebuilt master table no longer marks.
     */
    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.route.queryParams.subscribe(params => {

            const navItemAction = params[CmGeneralAgencyObjectComponent.cNavItemQueryParam];

            if (!navItemAction) {
                return;
            }

            const matchingItem = this.navigationItems.find(item => item.action === navItemAction);

            if (!matchingItem || matchingItem === this.activeNavigationItems) {
                return;
            }

            const currentAction = this.activeNavigationItems?.action;
            const leavingMaintenanceSection = currentAction === CmGeneralAgencyObjectComponent.cActionAgencyPartners;

            this.activeNavigationItems = matchingItem;

            if (leavingMaintenanceSection) {
                this.handleNumberDeselect();
            }
        });
    }

    /**
     * Builds the three master widgets once per agency. A repeated call for the same agency id is a
     * no-op, so a DTO reload — triggered by handleAgencyDataLoaded() — leaves the mounted lists
     * untouched instead of remounting them.
     */
    private createMasterWidgets(): void {

        const generalAgencyId = this.objectIdentifier?.objectId;

        if (!generalAgencyId || this.masterWidgetAgencyId === generalAgencyId) {
            return;
        }

        this.masterWidgetAgencyId = generalAgencyId;

        // a different agency invalidates every level-2 state
        this.handleNumberDeselect();

        // the freshly built header widget will report an initial load of its own
        this.agencyWidgetLoaded = false;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: entity data widget of the agency itself; serves as the header of the
         * maintenance section and as the content of the Stammdaten section.
         */
        this.generalAgencyWidget = WidgetFactory.createWidgetEntityData(
            'cm.general.agency.object.overview',
            'Generalagentur',
            'cm.GeneralAgency',
            generalAgencyId
        );

        this.createNumbersWidget(generalAgencyId);
        this.createAgencyAgentsWidget(generalAgencyId);
    }

    /**
     * Level 1: the partner numbers of this agency. Shows every status without a prefilter, so an
     * inactive number stays reachable. The base sorting is set once at creation, which keeps the
     * row order stable across in-place reloads.
     *
     * It sorts on the real entity attributes partnerId, then number, and NOT on the resolved
     * partner text. The resolved foreign key text is only reachable through the POSITIONAL alias
     * fkeyt_e_N.name, whose index the backend assigns over the resolvable @ManyToOne fields in
     * declaration order (QlDpEntityJdbcProviderImplResolver.PREFIX_AS) — adding or reordering a
     * relation on cm.GeneralAgencyNumber silently shifts it onto a different column, which is the
     * exact fragility documented in hb-config.page.ts. Stability was consciously preferred over
     * the alphabetical partner order; the grouping per partner is preserved either way.
     */
    private createNumbersWidget(generalAgencyId: number): void {

        const numberFilter = [
            FilterCriteria.create('e.generalAgencyId', FilterCriteria.cOperatorEqual, generalAgencyId)
        ];

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: row selectable table widget of cm.GeneralAgencyNumber; master of the level-2
         * assignment area.
         */
        this.numbersWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cm.general.agency.object.numbers',
            'Partnernummern',
            'cm.GeneralAgencyNumber',
            numberFilter,
            [new Sorting('e.partnerId', true), new Sorting('e.number', true)]
        );
        this.numbersWidget.setParamValue('selectionMode', 'rowSelect');
        this.numbersWidget.noDataText = 'Für diese Generalagentur ist noch keine Partnernummer angelegt.';

        const numberCreateFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'generalAgencyDtoId', fieldValue: generalAgencyId}
        ];
        this.numbersWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(numberCreateFields);
    }

    /**
     * Agency-wide agent assignments. They exist next to the number based assignments and are
     * deliberately not merged into one list.
     */
    private createAgencyAgentsWidget(generalAgencyId: number): void {

        const agentFilter = [
            FilterCriteria.create('e.generalAgencyId', FilterCriteria.cOperatorEqual, generalAgencyId)
        ];

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: table widget of the agency-wide cm.GeneralAgencyAgent assignments.
         */
        this.agentsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cm.general.agency.object.agents',
            'Agenturweite Berater',
            'cm.GeneralAgencyAgent',
            agentFilter,
            [new Sorting('e.id', true)]
        );
        this.agentsWidget.noDataText = 'Keine agenturweiten Berater zugeordnet. Über "Anlegen" wird ein ' +
            'Berater unabhängig von einer einzelnen Partnernummer zugewiesen.';

        const agentCreateFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'generalAgencyDtoId', fieldValue: generalAgencyId}
        ];
        this.agentsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(agentCreateFields);
    }

    /**
     * Level-1 selection handler. It is the single entry point of the level-2 area: it stores the
     * selected id, resets the tab to the first one and rebuilds all three detail widgets. Nothing
     * flows back from level 2 to level 1.
     *
     * The table widget emits this output for a right click (row context menu) and for a middle
     * click as well, and it re-emits for the row that is already selected. Neither is a new
     * selection, and acting on them would rebuild the detail widgets, re-issue their requests and
     * throw the user back to the first tab.
     */
    handleNumberSelect(event: ObjectIdentifierData | ObjectIdentifier): void {

        const eventType = (event as ObjectIdentifierData)?.event;

        if (eventType && eventType !== 'singleClick' && eventType !== 'singleClickCtrl') {
            return;
        }

        const objectId = event?.objectId;

        if (!objectId) {
            return;
        }

        if (this.selectedNumberId === Number(objectId)) {
            return;
        }

        this.selectedNumberId = Number(objectId);
        this.selectedNumberLabel = this.resolveNumberLabel(event);
        this.numberDetailTabIndex = 0;

        this.createNumberDetailWidgets(this.selectedNumberId);
    }

    /**
     * Builds the three level-2 widgets for the selected partner number. Every child defaults its
     * parent foreign key through the create toolbar, so a new record can never be attached to a
     * foreign parent.
     */
    private createNumberDetailWidgets(generalAgencyNumberId: number): void {

        const detailFilter = [
            FilterCriteria.create('e.generalAgencyNumberId', FilterCriteria.cOperatorEqual, generalAgencyNumberId)
        ];
        const detailCreateFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'generalAgencyNumberDtoId', fieldValue: generalAgencyNumberId}
        ];

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: table widget of the agent assignments of the selected partner number.
         */
        this.numberAgentsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cm.general.agency.object.number.agents',
            'Berater',
            'cm.GeneralAgencyNumberAgent',
            detailFilter,
            [new Sorting('e.id', true)]
        );
        this.numberAgentsWidget.noDataText = 'Dieser Partnernummer ist noch kein Berater zugeordnet. '
            + 'Über "Anlegen" wird ein Berater dieser Partnernummer zugewiesen.';
        this.numberAgentsWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(detailCreateFields);

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: table widget of the group restriction of the selected partner number.
         */
        this.numberGroupsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cm.general.agency.object.number.groups',
            'Sparten-Einschränkung',
            'cm.GeneralAgencyNumberGroup',
            detailFilter,
            [new Sorting('e.id', true)]
        );
        this.numberGroupsWidget.noDataText = 'Für diese Partnernummer ist keine Sparten-Einschränkung '
            + 'hinterlegt. Ohne Einschränkung gelten alle Sparten. Über "Anlegen" wird eine Sparte ein- '
            + 'oder ausgeschlossen.';
        this.numberGroupsWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(detailCreateFields);

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: table widget of the contract type restriction of the selected partner number.
         */
        this.numberContractTypesWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cm.general.agency.object.number.contract.types',
            'Vertragstyp-Einschränkung',
            'cm.GeneralAgencyNumberContractType',
            detailFilter,
            [new Sorting('e.id', true)]
        );
        this.numberContractTypesWidget.noDataText =
            'Für diese Partnernummer ist keine Vertragstyp-Einschränkung hinterlegt. Ohne Einschränkung '
            + 'gelten alle Vertragstypen. Über "Anlegen" wird ein Vertragstyp ein- oder ausgeschlossen.';
        this.numberContractTypesWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(detailCreateFields);
    }

    /**
     * Drops the level-2 selection when the selected partner number is deleted. Without it the
     * level-2 caption and the three detail tables keep rendering a parent that no longer exists,
     * and their create toolbars keep defaulting generalAgencyNumberDtoId to the deleted id.
     *
     * The deletion is observed on MvsObjectService.objectChangeEvent, which is the only channel the
     * framework broadcasts a deletion on: MvsCrudService.delete()/deleteSoft()/hardDelete() publish
     * it there, and every widget consumes it to refresh its list. The level-1 table itself is silent
     * — the row dialog it opens is given an empty change callback, and a table widget never emits
     * (onChangedObject), which only <mvs-widget-object> does.
     *
     * MvsCrudService puts the deleted record's id into "after" (it builds a bare DtoDetail holding
     * only the id); "before" is read as a fallback for the other producers of this event, which
     * assemble it through ObjectChangeInformation.create() and put the removed record there.
     *
     * A soft delete is covered as well, and that is the case that matters most: the server routes a
     * delete on a status carrying entity to a soft delete, and level 1 shows every status without a
     * prefilter, so the row survives and would otherwise still look like a selectable, valid parent.
     */
    private subscribeToNumberDeletion(): void {
        this.objectChangeSubscription = this.objectService.objectChangeEvent.subscribe(
            (change: ObjectChangeInformation) => {

                if (change?.objectType !== CmGeneralAgencyObjectComponent.cObjectTypeNumber) {
                    return;
                }

                if (change.action !== ObjectChangeInformationActionEnum.deleted) {
                    return;
                }

                const deletedId = change.after?.id ?? change.before?.id;

                if (deletedId && Number(deletedId) === this.selectedNumberId) {
                    this.handleNumberDeselect();
                }
            });
    }

    /**
     * Clears the whole level-2 state. Called on an agency change, when the selected number is
     * deleted and when the user leaves the maintenance section.
     */
    private handleNumberDeselect(): void {
        this.selectedNumberId = undefined;
        this.selectedNumberLabel = undefined;
        this.numberDetailTabIndex = 0;
        this.numberAgentsWidget = null;
        this.numberGroupsWidget = null;
        this.numberContractTypesWidget = null;
    }

    /**
     * PrimeNG models the tab value as string | number | undefined, so the coercion has to be
     * guarded: NaN matches none of the three panels and would leave the level-2 area blank with no
     * way back other than re-selecting the row. An unusable value falls back to the first tab.
     */
    onNumberDetailTabChange(value: string | number | undefined): void {
        const index = Number(value);
        this.numberDetailTabIndex = Number.isFinite(index) ? index : 0;
    }

    /**
     * Caption of the selected partner number, built from the row that travels with the selection
     * event. Reading the event payload keeps the one-way flow intact — no widget internals are
     * touched and no extra request is issued.
     *
     * The payload is a QL result row, NOT a CRUD DtoDetail: level 1 is a QL widget whose start
     * object carries the alias e, so every attribute is keyed 'e.<attribute>'. The resolved
     * partner text is deliberately left out of the caption — it exists only under the positional
     * alias fkeyt_e_N.name, whose index is backend assigned and shifts whenever a relation is
     * added (see createNumbersWidget).
     */
    private resolveNumberLabel(event: ObjectIdentifierData | ObjectIdentifier): string {

        const payload: any = (event as ObjectIdentifierData)?.data;
        const row: any = payload?.dto ?? payload;

        if (!row) {
            return 'Partnernummer';
        }

        const parts: string[] = [];
        const number = row['e.number'];
        const numberVM = row['e.numberVM'];

        if (number) {
            parts.push('Nr. ' + number);
        }
        if (numberVM) {
            parts.push('VM ' + numberVM);
        }

        return parts.length ? parts.join(' · ') : 'Partnernummer';
    }

    /**
     * True only for the one situation the restore is meant for: this component is going away
     * because the object page was CLOSED and the config page is coming back underneath it. All
     * three checks are required:
     *
     *  - the route check keeps the restore off every other page. Leaving to a foreign route
     *    destroys this component as well, and a merging navigation would then write the parameter
     *    into whatever URL is current at that moment.
     *  - the tid check separates a close from a mere object SWITCH. MvsGenericObjectPageComponent
     *    destroys and recreates the object component on every objectIdentifier change, so this
     *    destroy also runs when the user moves on to another object — and then tid still names
     *    that newly opened object. Restoring there would write the config page's section into the
     *    URL of an object page that is very much on screen. Only a close removes tid.
     *  - the action check keeps every foreign navItem untouched — the config page owns roughly
     *    twenty other sections on this very parameter, and none of them may be overwritten.
     *
     * The values are read from the router and not from the ActivatedRoute snapshot: this component
     * is created outside the router outlet, so its injected route is the root route, whose snapshot
     * is not a reliable picture of the URL at the moment of the destroy.
     */
    private isOwnNavItemActive(): boolean {

        const currentUrl = this.router.url;

        if (currentUrl.split(/[?#]/)[0] !== CmGeneralAgencyObjectComponent.cConfigPageRoute) {
            return false;
        }

        const queryParams = this.router.parseUrl(currentUrl).queryParams;

        // an object page is still open — a switch to another object, not a close
        if (queryParams?.[CmGeneralAgencyObjectComponent.cObjectPageQueryParam]) {
            return false;
        }

        const navItemAction = queryParams?.[CmGeneralAgencyObjectComponent.cNavItemQueryParam];

        return navItemAction === CmGeneralAgencyObjectComponent.cActionAgencyPartners
            || navItemAction === CmGeneralAgencyObjectComponent.cActionMasterData;
    }

    /**
     * Hands the shared ?navItem parameter back to the config page when this component goes away.
     *
     * Closing the object does not change the route — it only drops the tid parameters, which lets
     * the shell swap the object page back for the router outlet, i.e. for the config page. That
     * page then resolves the still present navItem of THIS component, finds no section of its own
     * for it and renders nothing at all. Restoring the Generalagentur section is what puts the user
     * back exactly where the object was opened from.
     *
     * The navigation replaces the current history entry instead of adding one: the parameter is a
     * repair of the state the user is already in, and a new entry would make the browser's back
     * button walk through it.
     *
     * ORDER — the call sits at the top of ngOnDestroy() but only SCHEDULES the navigation, one
     * microtask out, so it always lands AFTER super.ngOnDestroy() and after every synchronous
     * reaction of the shell to it. That order is the deliberate one, for three reasons:
     *
     *  1. super.ngOnDestroy() runs removeNavigationItems() → onNavigationItems.emit(null), which
     *     the shell answers in AppMainComponent.handleNavigationItems(). That method returns
     *     immediately for an empty item list and therefore never reaches navigateToRoute() in this
     *     pass — an earlier, synchronous restore would gain nothing and would only run while the
     *     shell is still tearing the object page down.
     *  2. Where the shell DOES reach navigateToRoute() — it restores the config page as the active
     *     page while closing — it derives selectedNavItem from the URL snapshot and then compares:
     *     a run before the restore keeps the unchanged value and returns without navigating, a run
     *     after it reads exactly the value written here and returns as well. The restore can
     *     therefore not be overwritten by the shell in either order.
     *  3. The destroy runs inside the change detection pass that the closing navigation itself
     *     triggered; starting the next navigation from there would re-enter the router while it is
     *     still settling.
     *
     * Because the URL may move between the scheduling and the execution — leaving the route or
     * opening another object destroys this component too — the guard is evaluated a second time
     * inside the microtask, against the URL that is current then. A navigation away from the route
     * and a switch to another object both fail that check, so the restore never writes into a
     * foreign URL and never disturbs the navigation in progress.
     *
     * Known residual: the shell reads the navItem for its own selectedNavItem (the highlight of the
     * left menu) from the URL snapshot while the object is being closed, i.e. strictly before any
     * code of this component can run. The restored section is rendered correctly, but the menu
     * entry stays unhighlighted until the next page activation. Correcting that needs the shell
     * itself (app.main.component.ts) and is out of scope for this component-local repair.
     */
    private restoreConfigPageNavItem(): void {

        if (!this.isOwnNavItemActive()) {
            return;
        }

        Promise.resolve().then(() => {

            if (!this.isOwnNavItemActive()) {
                return;
            }

            const queryParams = {
                [CmGeneralAgencyObjectComponent.cNavItemQueryParam]:
                    CmGeneralAgencyObjectComponent.cConfigPageNavItem
            };

            // an empty command list keeps the current path — only the query parameters change
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: queryParams,
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        });
    }

    override ngOnDestroy(): void {

        // schedules only — the navigation itself runs after super.ngOnDestroy() and after the
        // shell has answered its onNavigationItems(null); see restoreConfigPageNavItem()
        this.restoreConfigPageNavItem();

        this.queryParamSubscription?.unsubscribe();
        this.objectChangeSubscription?.unsubscribe();
        super.ngOnDestroy();
    }
}
